<template lang="pug">
.widget
  .widget-header.d-flex.justify-content-between.align-items-center
    h2.title {{ osOnly ? 'OS Targeting' : 'OS, ISP & Carrier Restrictions' }}
    .d-flex(v-if="isAutomaticOffer", style="gap: 12px", data-history-field="lock_fields")
      div
        small.text-muted.d-block Carrier
        toggle-button(
          v-model="lockCarrier",
          @change="emitLockChange",
          :labels="{checked: 'Manual', unchecked: 'API'}",
          :color="{checked: '#ffb047', unchecked: '#4a5cf2'}",
          :width="80",
          :height="20",
          :font-size="8"
        )
      div
        small.text-muted.d-block OS Version
        toggle-button(
          v-model="lockOsVersion",
          @change="emitLockChange",
          :labels="{checked: 'Manual', unchecked: 'API'}",
          :color="{checked: '#ffb047', unchecked: '#4a5cf2'}",
          :width="80",
          :height="20",
          :font-size="8"
        )

  .widget-body
    fieldset.border-0.p-0.m-0(v-if="!osOnly", :disabled="disabledIsp")
      .form-group(data-history-field="should_block_wifi")
        b-form-checkbox(v-model="shouldBlockWifi", @change="onShouldBlockWifiChange") Block WiFi

      h3.widget-section-title ISP Whitelist
      .form-group(data-history-field="isp_whitelist")
        label ISP Providers
        multiselect(:multiple="true", :options="isps", v-model="ispWhitelist",
          track-by="v", label="t", deselect-label="", select-label="", placeholder="Type to search",
        :internal-search="false", :loading="ispOptionsLoading", :options-limit="50",
        @search-change="debounceIspSearch", @input="emitChange", :disabled="disabledIsp")
      .form-group(data-history-field="allow_no_isp")
        b-form-checkbox(v-model="allowNoIsp", @change="onAllowNoIspChange") Allow No ISP

      h3.widget-section-title ISP Blacklist
      .form-group(data-history-field="isp_blacklist")
        label ISP Providers
        multiselect(:multiple="true", :options="isps", v-model="ispBlacklist",
          track-by="v", label="t", deselect-label="", select-label="", placeholder="Type to search",
        :internal-search="false", :loading="ispOptionsLoading", :options-limit="50",
        @search-change="debounceIspSearch", @input="emitChange", :disabled="disabledIsp")

    fieldset.border-0.p-0.m-0(v-if="!osOnly", :disabled="disabledIsp || isCarrierApiManaged")
      h3.widget-section-title Carrier Whitelist
      .form-group(data-history-field="carrier_whitelist")
        label Carrier Providers
          i.la.la-lock.ml-1(v-if="isCarrierApiManaged", v-b-tooltip.hover.right, title="Managed by API")
        multiselect(:multiple="true", :options="carriers", v-model="carrierWhitelist",
          track-by="v", label="t", deselect-label="", select-label="", placeholder="Type to search",
        :internal-search="false", :loading="carrierOptionsLoading", :options-limit="50",
        @search-change="debounceCarrierSearch", @input="emitChange", :disabled="disabledIsp || isCarrierApiManaged")
      .form-group(data-history-field="allow_no_carrier")
        b-form-checkbox(v-model="allowNoCarrier", @change="onAllowNoCarrierChange", :disabled="isCarrierApiManaged") Allow No Carrier

      h3.widget-section-title Carrier Blacklist
      .form-group(data-history-field="carrier_blacklist")
        label Carrier Providers
          i.la.la-lock.ml-1(v-if="isCarrierApiManaged", v-b-tooltip.hover.right, title="Managed by API")
        multiselect(:multiple="true", :options="carriers", v-model="carrierBlacklist",
          track-by="v", label="t", deselect-label="", select-label="", placeholder="Type to search",
        :internal-search="false", :loading="carrierOptionsLoading", :options-limit="50",
        @search-change="debounceCarrierSearch", @input="emitChange", :disabled="disabledIsp || isCarrierApiManaged")

    fieldset.border-0.p-0.m-0(:disabled="disabledOs || isOsVersionApiManaged")
      h3.widget-section-title(v-if="!osOnly") OS Targeting
        i.la.la-lock.ml-1(v-if="isOsVersionApiManaged", v-b-tooltip.hover.right, title="Managed by API")
      .form-group(data-history-field="os_targeting")
        label OS
        select.form-control(v-model="osTargeting", @change="onOsTargetingChange")
          option(v-for="os in oses" :value="os.value") {{ os.label }}
      .form-group(v-if="osTargeting")
        label.font-weight-bold Allowed version range (DEPRECATED)
        .row
          .col-md-6(data-history-field="os_targeting_version_min")
            label Minimum version
            input.form-control(v-model="osTargetingVersionMin", placeholder="No limit", @change="emitChange")
          .col-md-6(data-history-field="os_targeting_version_max")
            label Maximum version
            input.form-control(v-model="osTargetingVersionMax", placeholder="No limit", @change="emitChange")
      .form-group(v-if="osTargeting", data-history-field="os_version_blacklist")
        os-targeting-version-blacklist(:value="osVersionBlacklist", @input="onOsVersionBlacklistInput")

    a(href="javascript:void(0);", v-if="!osOnly && osTargeting", @click="shouldShowAdvancedFilters = !shouldShowAdvancedFilters")
      span(v-if="!shouldShowAdvancedFilters")
        i.la.la-angle-down
      span(v-if="shouldShowAdvancedFilters")
        i.la.la-angle-up
      span Advanced

    fieldset.border-0.p-0.m-0(v-if="!osOnly", :disabled="disabledOs || disabledIsp")
      .form-group(v-if="shouldShowAdvancedFilters", data-history-field="advanced_targeting_blacklist")
        h3.widget-section-title Advanced

        div(v-if="advancedTargetingBlacklist.length")
          label.font-weight-bold Blacklist
          .d-flex.flex-wrap.gap-1.mb-2
            b-badge.fs-8(
              pill,
              variant="danger",
              v-for="(o, index) in advancedTargetingBlacklist",
              :key="'bl-' + index"
            )
              span.mr-1 {{ o.os_version_range.min }} - {{ o.os_version_range.max }} / {{ ispsHash[o.isp_id] }} / {{ carriersHash[o.carrier_id] }}
              i.la.la-times-circle.clickable(@click="removeAdvancedTargetingElement({type:'blacklist', index:index})")
        div(v-if="advancedTargetingWhitelist.length", data-history-field="advanced_targeting_whitelist")
          label.font-weight-bold Whitelist
          .d-flex.flex-wrap.gap-1.mb-2
            b-badge.fs-8(
              pill,
              variant="success",
              v-for="(o, index) in advancedTargetingWhitelist",
              :key="'wl-' + index"
            )
              span.mr-1 {{ o.os_version_range.min }} - {{ o.os_version_range.max }} / {{ ispsHash[o.isp_id] }} / {{ carriersHash[o.carrier_id] }}
              i.la.la-times-circle.clickable(@click="removeAdvancedTargetingElement({type:'whitelist', index:index})")

        .form-group
          label.font-weight-bold OS Version Range
          .row
            .col-md-6
              label Minimum version
              input.form-control(v-model="advancedTargetingElementForm.os_version_range.min", placeholder="No limit")
            .col-md-6
              label Maximum version
              input.form-control(v-model="advancedTargetingElementForm.os_version_range.max", placeholder="No limit")
        .form-group
          label.font-weight-bold ISP & Carrier
          .row
            .col-md-6
              label ISP Provider
              multiselect(:multiple="false", :options="isps", v-model="advancedTargetingElementForm.isp",
                track-by="v", label="t", deselect-label="", select-label="", :disabled="disabledOs || disabledIsp", placeholder="Type to search",
              :internal-search="false", :loading="ispOptionsLoading", :options-limit="50",
              @search-change="debounceIspSearch")
            .col-md-6
              label Carrier Provider
              multiselect(:multiple="false", :options="carriers", v-model="advancedTargetingElementForm.carrier",
                track-by="v", label="t", deselect-label="", select-label="", :disabled="disabledOs || disabledIsp", placeholder="Type to search",
              :internal-search="false", :loading="carrierOptionsLoading", :options-limit="50",
              @search-change="debounceCarrierSearch")
        .form-group.d-flex.gap-2
          button.btn.btn-success(type="button", @click="addAdvancedTargetingElement('whitelist')") Add to whitelist
          button.btn.btn-danger(type="button", @click="addAdvancedTargetingElement('blacklist')") Add to blacklist
</template>

<style lang="scss"></style>

<script>
import Vue from 'vue';
import { debounce } from 'vue-debounce';

/**
 * @typedef {Object} Option
 * @property {number} v
 * @property {string} t
 */

const OPTIONS_LIMIT = 50;

export default {
  name: 'os-network-targeting',
  emits: ['input'],
  props: {
    value: {
      type: [Object],
      required: true,
    },
    disabledOs: {
      type: Boolean,
      default: false,
    },
    disabledIsp: {
      type: Boolean,
      default: false,
    },
    osOnly: {
      type: Boolean,
      default: false,
    },
    isAutomaticOffer: {
      type: Boolean,
      default: false,
    },
    lockFields: {
      type: Object,
      default: () => ({}),
    },
  },
  created() {
    this.debounceIspSearch = debounce(this.searchIsp.bind(this), 300);
    this.debounceCarrierSearch = debounce(this.searchCarrier.bind(this), 300);
  },
  async mounted() {
    this.syncFromValue(this.value);
    if (!this.osOnly) {
      await this.hydrateSelected();
    }
    this.allowNoIsp = !!this.value.allow_no_isp;
    this.allowNoCarrier = !!this.value.allow_no_carrier;
    this.lockCarrier = !!this.lockFields.carrier;
    this.lockOsVersion = !!this.lockFields.os_version;
  },
  computed: {
    isCarrierApiManaged() {
      return this.isAutomaticOffer && !this.lockCarrier;
    },
    isOsVersionApiManaged() {
      return this.isAutomaticOffer && !this.lockOsVersion;
    },
    carriersHash() {
      return Vue.ovData.geo.carrier._hash;
    },
    ispsHash() {
      return Vue.ovData.geo.isp._hash;
    },
  },
  watch: {
    'value.os_targeting': {
      handler(osTargeting) {
        if (osTargeting !== null) {
          return;
        }

        this.value.os_targeting_version_min = null;
        this.value.os_targeting_version_max = null;
        this.value.os_version_blacklist = [];
      },
    },
    lockFields: {
      handler(lockFields) {
        this.lockCarrier = !!lockFields?.carrier;
        this.lockOsVersion = !!lockFields?.os_version;
      },
      deep: true,
    },
  },
  data() {
    return {
      /** @type {Option[]} */
      carriers: [],
      /** @type {Option[]} */
      isps: [],
      ispOptionsLoading: false,
      carrierOptionsLoading: false,
      oses: [
        { value: null, label: 'No restrictions' },
        { value: 'android', label: 'Android' },
        { value: 'ios', label: 'iOS' },
      ],
      /** @type {Option[]} */
      carrierWhitelist: [],
      /** @type {Option[]} */
      carrierBlacklist: [],
      /** @type {Option[]} */
      ispWhitelist: [],
      /** @type {Option[]} */
      ispBlacklist: [],
      allowNoIsp: false,
      allowNoCarrier: false,
      shouldBlockWifi: false,
      osTargeting: null,
      osTargetingVersionMin: null,
      osTargetingVersionMax: null,
      osVersionBlacklist: [],
      advancedTargetingWhitelist: [],
      advancedTargetingBlacklist: [],
      lockCarrier: false,
      lockOsVersion: false,
      shouldShowAdvancedFilters: false,
      advancedTargetingElementForm: {
        os_version_range: {
          /** @type {string | null} */
          min: null,
          /** @type {string | null} */
          max: null,
        },
        /** @type {Option | null} */
        carrier: null,
        /** @type {Option | null} */
        isp: null,
      },
    };
  },
  methods: {
    syncFromValue(v) {
      this.shouldBlockWifi = !!v.should_block_wifi;
      this.osTargeting = v.os_targeting ?? null;
      this.osTargetingVersionMin = v.os_targeting_version_min ?? null;
      this.osTargetingVersionMax = v.os_targeting_version_max ?? null;
      this.osVersionBlacklist = Array.isArray(v.os_version_blacklist)
        ? [...v.os_version_blacklist]
        : [];
      this.advancedTargetingWhitelist = Array.isArray(v.advanced_targeting_whitelist)
        ? [...v.advanced_targeting_whitelist]
        : [];
      this.advancedTargetingBlacklist = Array.isArray(v.advanced_targeting_blacklist)
        ? [...v.advanced_targeting_blacklist]
        : [];
      this.allowNoIsp = !!v.allow_no_isp;
      this.allowNoCarrier = !!v.allow_no_carrier;
    },

    emitLockChange() {
      this.$emit('update-lock-fields', {
        carrier: !!this.lockCarrier,
        os_version: !!this.lockOsVersion,
      });
    },
    emitChange() {
      this.$emit('input', {
        os_targeting: this.osTargeting,
        os_targeting_version_min: this.osTargetingVersionMin,
        os_targeting_version_max: this.osTargetingVersionMax,
        os_version_blacklist: this.osVersionBlacklist,
        carrier_whitelist: this.carrierWhitelist.map(({ v }) => v),
        carrier_blacklist: this.carrierBlacklist.map(({ v }) => v),
        isp_whitelist: this.ispWhitelist.map(({ v }) => v),
        isp_blacklist: this.ispBlacklist.map(({ v }) => v),
        allow_no_isp: this.allowNoIsp,
        allow_no_carrier: this.allowNoCarrier,
        should_block_wifi: this.shouldBlockWifi,
        advanced_targeting_whitelist: this.advancedTargetingWhitelist,
        advanced_targeting_blacklist: this.advancedTargetingBlacklist,
      });
    },

    /** b-form-checkbox @change may fire before v-model settles — apply payload explicitly */
    onShouldBlockWifiChange(val) {
      if (typeof val === 'boolean') {
        this.shouldBlockWifi = val;
      }
      this.emitChange();
    },
    onAllowNoIspChange(val) {
      if (typeof val === 'boolean') {
        this.allowNoIsp = val;
      }
      this.emitChange();
    },
    onAllowNoCarrierChange(val) {
      if (typeof val === 'boolean') {
        this.allowNoCarrier = val;
      }
      this.emitChange();
    },

    onOsTargetingChange() {
      if (this.osTargeting === null) {
        this.osTargetingVersionMin = null;
        this.osTargetingVersionMax = null;
        this.osVersionBlacklist = [];
      }
      this.emitChange();
    },

    onOsVersionBlacklistInput(next) {
      this.osVersionBlacklist = Array.isArray(next) ? next : [];
      this.emitChange();
    },

    /**
     * Load labels for already-selected ISP/carrier IDs without fetching the full lists.
     */
    async hydrateSelected() {
      const advanced = [
        ...this.advancedTargetingWhitelist,
        ...this.advancedTargetingBlacklist,
      ];
      const ispIds = [
        ...(this.value.isp_whitelist || []),
        ...(this.value.isp_blacklist || []),
        ...advanced.map((o) => o.isp_id),
      ];
      const carrierIds = [
        ...(this.value.carrier_whitelist || []),
        ...(this.value.carrier_blacklist || []),
        ...advanced.map((o) => o.carrier_id),
      ];

      const [ispOptions, carrierOptions] = await Promise.all([
        Vue.ovData.geo.isp.getSelectedOptions(ispIds),
        Vue.ovData.geo.carrier.getSelectedOptions(carrierIds),
      ]);

      const ispById = Object.fromEntries(ispOptions.map((o) => [o.v, o]));
      const carrierById = Object.fromEntries(carrierOptions.map((o) => [o.v, o]));

      this.ispWhitelist = (this.value.isp_whitelist || []).map((id) => ispById[id]).filter(Boolean);
      this.ispBlacklist = (this.value.isp_blacklist || []).map((id) => ispById[id]).filter(Boolean);
      this.carrierWhitelist = (this.value.carrier_whitelist || []).map((id) => carrierById[id]).filter(Boolean);
      this.carrierBlacklist = (this.value.carrier_blacklist || []).map((id) => carrierById[id]).filter(Boolean);
    },

    async searchIsp(query) {
      this.ispOptionsLoading = true;
      try {
        this.isps = await Vue.ovData.geo.isp.getOptions({ keyword: query, limit: OPTIONS_LIMIT });
      } catch (e) {
        console.error(e);
      }
      this.ispOptionsLoading = false;
    },

    async searchCarrier(query) {
      this.carrierOptionsLoading = true;
      try {
        this.carriers = await Vue.ovData.geo.carrier.getOptions({ keyword: query, limit: OPTIONS_LIMIT });
      } catch (e) {
        console.error(e);
      }
      this.carrierOptionsLoading = false;
    },

    /**
     * Add an element to the advanced targeting list.
     * @param {'whitelist'|'blacklist'} type
     */
    addAdvancedTargetingElement(type) {
      if (this.disabledOs || this.disabledIsp) return;
      const { os_version_range, carrier, isp } = this.advancedTargetingElementForm;
      if (
        os_version_range.min === null
        || os_version_range.max === null
        || carrier === null
        || isp === null
      ) {
        this.$ovNotify.error('Please fill all advanced targeting fields');
        return;
      }

      if (Number.isNaN(this.versionToNumber(os_version_range.min))) {
        return this.$ovNotify.error('OS minimum version is not a valid version number');
      }
      if (Number.isNaN(this.versionToNumber(os_version_range.max))) {
        return this.$ovNotify.error('OS maximum version is not a valid version number');
      }

      if (os_version_range.min && os_version_range.max) {
        const minNumber = this.versionToNumber(os_version_range.min);
        const maxNumber = this.versionToNumber(os_version_range.max);
        if (minNumber > maxNumber) {
          this.$ovNotify.error('OS minimum version must be less than OS maximum version');
          return;
        }
      }

      const entry = {
        os_version_range: { ...os_version_range },
        carrier_id: carrier.v,
        isp_id: isp.v,
      };
      if (type === 'whitelist') {
        this.advancedTargetingWhitelist = [...this.advancedTargetingWhitelist, entry];
      } else {
        this.advancedTargetingBlacklist = [...this.advancedTargetingBlacklist, entry];
      }
      this.emitChange();
      this.advancedTargetingElementForm = {
        os_version_range: {
          min: null,
          max: null,
        },
        carrier: null,
        isp: null,
      };
    },

    /**
     * Remove an element from the advanced targeting list.
     * @param {{type: 'blacklist' | 'whitelist', index: number}} param
     */
    removeAdvancedTargetingElement({ type, index }) {
      if (this.disabledOs || this.disabledIsp) return;
      if (type === 'whitelist') {
        this.advancedTargetingWhitelist = this.advancedTargetingWhitelist.filter((_, i) => i !== index);
      } else {
        this.advancedTargetingBlacklist = this.advancedTargetingBlacklist.filter((_, i) => i !== index);
      }
      this.emitChange();
    },

    /**
     * Convert a version string into a number.
     * @param {string} version - The version string to convert in the format "major.minor.patch.build".
     * @returns {number} - The version number.
     */
    versionToNumber(version) {
      const numbers = version.split('.').map(Number);

      while (numbers.length < 4) {
        for (let i = 0; i < 4 - numbers.length; i++) {
          numbers.push(0);
        }
      }

      const [major, minor, patch, build] = numbers;
      return major * 1000000 + minor * 10000 + patch * 100 + build;
    },
  },
};
</script>
